import { Injectable } from '@angular/core';
import {BehaviorSubject, merge, Observable, Subject} from 'rxjs';
import {distinctUntilChanged, map, scan, shareReplay} from 'rxjs/operators';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';

import {ContactSearchOptionsRequest} from '../../models/contactSearchOptionsRequest.model';
import {AppQuery, AppService} from '../../../state';
import { PaymentType } from '../../enums/payment-type.enum';

@Injectable({
  providedIn: 'root'
})
export class RecipientContactsTableService extends OnDestroyMixin{
  private _refreshData$ = new Subject<void>();
  refreshData$ = this._refreshData$.asObservable();

  // filters
  private pageSize$ = new Subject<number>();
  private pageNumber$ = new Subject<number>();
  private tagsFilter$ = new Subject<number[]>();
  private paymentTypeFilter$ = new BehaviorSubject<PaymentType>(PaymentType.None);
  private textFilter$ = new Subject<string>();

  private pageNumberSource$ = this.pageNumber$.pipe(map(pageNumber => ({ pageNumber }) as ContactSearchOptionsRequest));
  private pageSizeSource$ = this.pageSize$.pipe(map(pageSize => ({ pageSize }) as ContactSearchOptionsRequest));
  private textFilterSource$ = this.textFilter$.pipe(map(filter => filter.trim()), distinctUntilChanged(), map(filter => ({ filter }) as ContactSearchOptionsRequest));
  private tagsSource$: Observable<ContactSearchOptionsRequest> = this.tagsFilter$.pipe(map(tags => ({ tags: tags || [] }) as ContactSearchOptionsRequest));
  private paymentTypeSource$: Observable<ContactSearchOptionsRequest> = this.paymentTypeFilter$.pipe(
    distinctUntilChanged(),
    map(paymentType => ({ paymentType: paymentType || PaymentType.None}) as ContactSearchOptionsRequest));
  private uiStateSource$: Observable<ContactSearchOptionsRequest> = this.appQuery.gpUiPrefs.pipe(
    distinctUntilChanged((prev, curr) => {
      return prev.recipientContacts.sort.orderBy === curr.recipientContacts.sort.orderBy &&
             prev.recipientContacts.sort.direction === curr.recipientContacts.sort.direction;
    }),
    map(state => state.recipientContacts.sort),
    map(sort => ({ orderBy: sort.orderBy, sortOrder: sort.direction }) as ContactSearchOptionsRequest));

  // Merged source of search options
  private searchOptionsSources$: Observable<ContactSearchOptionsRequest> = merge(
    this.uiStateSource$,
    this.textFilterSource$,
    this.tagsSource$,
    this.pageNumberSource$,
    this.pageSizeSource$,
    this.paymentTypeSource$);

  // Search options state
  searchOptions$: Observable<ContactSearchOptionsRequest> = this.searchOptionsSources$
    .pipe(
      untilComponentDestroyed(this),
      scan((acc, value) => {
        // Whenever somehting changes, unless it's the pagenumber, return to the first page
        const pageNumber = value.pageNumber === undefined ? 0 : value.pageNumber;
        return { ...acc, ...value, pageNumber } as ContactSearchOptionsRequest;
      }, new ContactSearchOptionsRequest()),
      shareReplay(1)
    );

  constructor(private appService: AppService, private appQuery: AppQuery) {
    super();
  }

  refresh() {
    this._refreshData$.next();
  }

  sort(orderBy: string, sortOrder: string) {
    this.appService.updateRecipientContactsSortState({ orderBy, direction: sortOrder === 'desc' ? 'desc' : 'asc' });
  }

  updateFilter(term: string) {
    this.textFilter$.next(term);
  }

  updateTagsFilter(tags: number[]) {
    this.tagsFilter$.next(tags);
  }

  updatePaymentTypeFilter(paymentType: PaymentType) {
    this.paymentTypeFilter$.next(paymentType);
  }

  updatePageSize(pageSize: number) {
    this.pageSize$.next(pageSize);
  }

  updatePageNumber(page: number) {
    this.pageNumber$.next(page);
  }
}
